import axios from 'axios';

const RAW_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const API_BASE = RAW_API_URL.endsWith('/api') ? RAW_API_URL : `${RAW_API_URL}/api`;

const getToken = () => localStorage.getItem('token');

const authHeaders = () => {
  const token = getToken();
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return { headers };
};

const handleApiError = (error, defaultMsg = 'Une erreur est survenue') => {
  const message = error.response?.data?.message || error.response?.data?.error || error.message || defaultMsg;
  const status = error.response?.status;
  const data = error.response?.data;
  const customError = new Error(message);
  customError.status = status;
  customError.data = data;
  throw customError;
};

// ============================================================================
// 1. OPPORTUNITÉS D'APPELS D'OFFRES (/api/tenders)
// ============================================================================

/**
 * Récupère les métriques globales du dashboard marchés
 */
export const getTenderDashboardMetrics = async () => {
  try {
    const res = await axios.get(`${API_BASE}/tenders/dashboard/metrics`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer les métriques des appels d\'offres');
  }
};

/**
 * Récupère la liste paginée et filtrée des opportunités
 */
export const getTenderOpportunities = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/tenders`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer la liste des marchés publics');
  }
};

/**
 * Récupère le détail complet d'une opportunité
 */
export const getTenderOpportunityById = async (id) => {
  try {
    const res = await axios.get(`${API_BASE}/tenders/${id}`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer le détail de l\'appel d\'offres');
  }
};

/**
 * Met à jour le statut commercial interne (NEW, TO_REVIEW, INTERESTED, etc.)
 */
export const updateTenderCommercialStatus = async (id, payload) => {
  try {
    const res = await axios.patch(`${API_BASE}/tenders/${id}/status`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de mettre à jour le statut du marché');
  }
};

/**
 * Active / désactive le favori
 */
export const toggleTenderFavorite = async (id) => {
  try {
    const res = await axios.post(`${API_BASE}/tenders/${id}/toggle-favorite`, {}, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de modifier le favori');
  }
};

/**
 * Ajoute une note interne de suivi commercial
 */
export const addTenderInternalNote = async (id, noteText) => {
  try {
    const res = await axios.post(`${API_BASE}/tenders/${id}/notes`, { noteText }, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible d\'enregistrer la note interne');
  }
};

/**
 * Soumet un feedback de correction administrateur sur l'évaluation IA
 */
export const submitTenderAdminFeedback = async (id, payload) => {
  try {
    const res = await axios.post(`${API_BASE}/tenders/${id}/feedback`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible d\'enregistrer le feedback sur la pertinence');
  }
};

// ============================================================================
// 2. ALERTES MARCHÉS (/api/tenders/alerts)
// ============================================================================

/**
 * Liste des alertes marchés (J-7, J-3, reports de délais, fortes opportunités)
 */
export const getTenderAlerts = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/tenders/alerts`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer les alertes marchés');
  }
};

/**
 * Marque une alerte comme acquittée
 */
export const dismissTenderAlert = async (alertId) => {
  try {
    const res = await axios.post(`${API_BASE}/tenders/alerts/${alertId}/dismiss`, {}, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible d\'acquitter l\'alerte');
  }
};

// ============================================================================
// 3. CONTRÔLE ET PILOTAGE DU TENDER AGENT (/api/tenders/agent-admin)
// ============================================================================

/**
 * Statut en temps réel du Tender Agent (FastAPI via Express)
 */
export const getTenderAgentStatus = async () => {
  try {
    const res = await axios.get(`${API_BASE}/tenders/agent-admin/status`, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer le statut du Tender Agent');
  }
};

/**
 * Déclenchement manuel d'une collecte (DISCOVERY, DRY_RUN, INGEST)
 */
export const triggerTenderAgent = async (payload = { mode: 'DRY_RUN', limit: 20, confirmed: false }) => {
  try {
    const res = await axios.post(`${API_BASE}/tenders/agent-admin/trigger`, payload, authHeaders());
    return res.data;
  } catch (err) {
    handleApiError(err, 'Échec du déclenchement de la collecte du Tender Agent');
  }
};

/**
 * Historique des exécutions du Tender Agent
 */
export const getTenderAgentHistory = async (params = {}) => {
  try {
    const res = await axios.get(`${API_BASE}/tenders/agent-admin/history`, {
      params,
      ...authHeaders(),
    });
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de récupérer l\'historique des exécutions du Tender Agent');
  }
};

/**
 * Active ou désactive la planification automatique
 */
export const toggleTenderAgentSchedule = async (enabled) => {
  try {
    const res = await axios.post(
      `${API_BASE}/tenders/agent-admin/toggle-schedule`,
      { enabled },
      authHeaders()
    );
    return res.data;
  } catch (err) {
    handleApiError(err, 'Impossible de modifier la planification automatique');
  }
};

